import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import PrintWarningModal from '../../../../components/PrintWarningModal';
import {
  salesInvoiceService,
  SalesInvoiceDTO,
  SalesInvoiceStatus,
  SalesInvoicePaymentDTO,
  RecordPaymentRequest,
} from '../../../../services/salesInvoiceService';
import paymentTypeService, { PaymentType } from '../../../../services/paymentTypeService';
import { printerService, PrinterProfile, PrinterType } from '../../../../services/printerService';
import { PrintResult, PrintOutputType } from '../../../../types';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import { useCompany } from '../../../../context/CompanyContext';
import { useAuth } from '../../../../context/AuthContext';
import { Permissions } from '../../../../constants/Permissions';
import { parseApiError } from '../../../../utils/errorUtils';
import './SalesInvoicePage.css';
import { CircleDollarSign, CreditCard, Check, AlertCircle, Send, PenLine, XCircle, Download, Printer, FileText, List, Calendar, CalendarClock, Ban } from 'lucide-react';

// ─── Status helpers ───────────────────────────────────────────────────────────

const STATUS_CHIP_CLASS: Record<SalesInvoiceStatus, string> = {
  Draft:        'si-status-chip si-status-chip--draft',
  Issued:       'si-status-chip si-status-chip--issued',
  PartiallyPaid:'si-status-chip si-status-chip--partiallypaid',
  Paid:         'si-status-chip si-status-chip--paid',
  Overdue:      'si-status-chip si-status-chip--overdue',
  Voided:       'si-status-chip si-status-chip--voided',
  Cancelled:    'si-status-chip si-status-chip--cancelled',
};

// ─── Steps ────────────────────────────────────────────────────────────────────

const STEPS: { key: SalesInvoiceStatus[]; label: string }[] = [
  { key: ['Draft'],                        label: 'Draft' },
  { key: ['Issued'],                       label: 'Issued' },
  { key: ['PartiallyPaid'],               label: 'Partially Paid' },
  { key: ['Paid'],                         label: 'Paid' },
];

const SalesInvoiceDetailPage: React.FC = () => {
  const { id }     = useParams<{ id: string }>();
  const navigate   = useNavigate();
  const { t }      = useTranslation();
  const { defaultPrintOutput } = useCompany();
  const { hasPermission } = useAuth();

  const [invoice,        setInvoice]        = useState<SalesInvoiceDTO | null>(null);
  const [loading,        setLoading]        = useState(true);
  const [actionLoading,  setActionLoading]  = useState(false);
  const [error,          setError]          = useState<string | null>(null);
  const [successMsg,     setSuccessMsg]     = useState<string | null>(null);

  // Void
  const [showVoidModal,  setShowVoidModal]  = useState(false);
  const [voidReason,     setVoidReason]     = useState('');
  const [voidError,      setVoidError]      = useState<string | null>(null);

  // Payment history
  const [payments,       setPayments]       = useState<SalesInvoicePaymentDTO[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  // Active payment types (loaded once, used in the Record Payment form)
  const [activePaymentTypes, setActivePaymentTypes] = useState<PaymentType[]>([]);

  // Payment form
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentData,     setPaymentData]     = useState<RecordPaymentRequest>({
    amountPaid:    0,
    paymentDate:   new Date().toISOString().slice(0, 10),
    paymentMethod: '',
    referenceNumber: '',
    notes:         '',
  });
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [amountTendered, setAmountTendered] = useState<number>(0);

  // Print / reprint
  const [reprintLoading, setReprintLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [showPrintWarning, setShowPrintWarning] = useState(false);
  const [printWarnings, setPrintWarnings] = useState<string[]>([]);

  // Printer profiles — offered as a choice when the bill goes to a physical
  // printer. Empty (e.g. none configured) means the backend default is used.
  const [printerProfiles, setPrinterProfiles] = useState<PrinterProfile[]>([]);
  const [selectedPrinterId, setSelectedPrinterId] = useState<number | undefined>(undefined);

  const usesPosPrinter =
    defaultPrintOutput === PrintOutputType.POS_PRINTER || defaultPrintOutput === PrintOutputType.BOTH;

  useEffect(() => {
    if (!usesPosPrinter) return;
    printerService.getProfilesForPrinting()
      .then((res) => {
        const profiles = res.data.data ?? [];
        setPrinterProfiles(profiles);
        const def = profiles.find((p) => p.isDefault) ?? profiles[0];
        setSelectedPrinterId(def?.id);
      })
      .catch(() => { /* non-critical — backend falls back to the default printer */ });
  }, [usesPosPrinter]);

  const loadPayments = useCallback(async () => {
    if (!id) return;
    setPaymentsLoading(true);
    try {
      const res = await salesInvoiceService.getPayments(id);
      const data = res.data?.data ?? res.data ?? [];
      setPayments(Array.isArray(data) ? data : []);
    } catch {
      // Non-critical — silently ignore
    } finally {
      setPaymentsLoading(false);
    }
  }, [id]);

  useEffect(() => { if (id) { loadInvoice(); loadPayments(); } }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load active payment types once for the Record Payment dropdown
  useEffect(() => {
    paymentTypeService.getActivePaymentTypes('Sales')
      .then((types) => {
        if (Array.isArray(types) && types.length > 0) {
          setActivePaymentTypes(types);
          // Pre-select the default type (or first non-Credit type, since record-payment is for partial/manual payments)
          const def = types.find((pt) => pt.isDefault && pt.paymentCategory !== 'Credit') ?? types.find((pt) => pt.paymentCategory !== 'Credit') ?? types[0];
          setPaymentData((prev) => ({ ...prev, paymentMethod: def?.code ?? '' }));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(null), 4000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  const loadInvoice = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await salesInvoiceService.getSalesInvoiceById(id!);
      setInvoice(res.data?.data ?? null);
    } catch {
      setError(t('salesInvoice.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleIssue = async () => {
    if (!invoice) return;
    setActionLoading(true);
    setError(null);
    try {
      await salesInvoiceService.issueSalesInvoice(invoice.id);
      setSuccessMsg(t('salesInvoice.issuedSuccess'));
      loadInvoice();
    } catch {
      setError(t('salesInvoice.failedToIssue'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!invoice) return;
    if (!window.confirm(t('salesInvoice.cancelConfirm'))) return;
    setActionLoading(true);
    setError(null);
    try {
      await salesInvoiceService.cancelSalesInvoice(invoice.id);
      setSuccessMsg(t('salesInvoice.cancelledSuccess'));
      loadInvoice();
    } catch {
      setError(t('salesInvoice.failedToCancel'));
    } finally {
      setActionLoading(false);
    }
  };

  // ── Void ────────────────────────────────────────────────────────────────
  // Voiding reverses the sale and its payments, so it asks for a reason rather than a
  // bare confirm — the reason is stored on the invoice and reported on.
  const handleVoid = async () => {
    if (!invoice) return;
    const reason = voidReason.trim();
    if (!reason) {
      setVoidError(t('salesInvoice.voidReasonRequired'));
      return;
    }
    setActionLoading(true);
    setVoidError(null);
    setError(null);
    try {
      await salesInvoiceService.voidSalesInvoice(invoice.id, reason);
      setShowVoidModal(false);
      setVoidReason('');
      setSuccessMsg(t('salesInvoice.voidedSuccess'));
      loadInvoice();
    } catch (err) {
      // Surface the server's reason verbatim — it explains closed trading days and
      // already-voided invoices far better than a generic message.
      setVoidError(parseApiError(err, t('salesInvoice.failedToVoid')).message);
    } finally {
      setActionLoading(false);
    }
  };

  const isCashPaymentMethod =
    activePaymentTypes.find((pt) => pt.code === paymentData.paymentMethod)?.paymentCategory === 'Cash';

  const paymentMethodName = (code: string) =>
    activePaymentTypes.find((pt) => pt.code === code)?.name ?? code;

  const handleRecordPayment = async () => {
    if (!invoice) return;
    if (isCashPaymentMethod) {
      if (!amountTendered || amountTendered <= 0) {
        setPaymentError(t('salesInvoice.paymentAmountRequired'));
        return;
      }
      setActionLoading(true);
      setPaymentError(null);
      setError(null);
      try {
        const amountToApply = Math.min(amountTendered, invoice.balanceDue);
        await salesInvoiceService.recordPayment(invoice.id, {
          ...paymentData,
          amountPaid: amountToApply,
          tenderedAmount: amountTendered,
        });
        setSuccessMsg(t('salesInvoice.paymentRecordedSuccess'));
        setShowPaymentForm(false);
        setAmountTendered(0);
        loadInvoice();
        loadPayments();
      } catch {
        setError(t('salesInvoice.failedToRecordPayment'));
      } finally {
        setActionLoading(false);
      }
      return;
    }

    if (!paymentData.amountPaid || paymentData.amountPaid <= 0) {
      setPaymentError(t('salesInvoice.paymentAmountRequired'));
      return;
    }
    setActionLoading(true);
    setPaymentError(null);
    setError(null);
    try {
      await salesInvoiceService.recordPayment(invoice.id, paymentData);
      setSuccessMsg(t('salesInvoice.paymentRecordedSuccess'));
      setShowPaymentForm(false);
      loadInvoice();
      loadPayments();
    } catch {
      setError(t('salesInvoice.failedToRecordPayment'));
    } finally {
      setActionLoading(false);
    }
  };

  /** Open PDF in a new browser tab (popup-blocker-safe: open tab synchronously). */
  const openPdfTab = async () => {
    if (!invoice) return;
    const pdfTab = window.open('', '_blank');
    try {
      await salesInvoiceService.viewInvoicePdf(invoice.id, invoice.invoiceNumber, pdfTab);
    } catch {
      if (pdfTab && !pdfTab.closed) pdfTab.close();
      setError(t('salesInvoice.failedToPrint') || 'Failed to open PDF');
    }
  };

  /** Send to POS printer via backend and handle warnings. */
  const sendToPosPrinter = async () => {
    if (!invoice) return;
    setPrintLoading(true);
    setError(null);
    try {
      const res = await salesInvoiceService.printInvoice(
        invoice.id, PrintOutputType.POS_PRINTER, selectedPrinterId);
      if (res.data) {
        const result = res.data as PrintResult;
        if (result.warnings && result.warnings.length > 0) {
          setPrintWarnings(result.warnings);
          setShowPrintWarning(true);
        } else {
          setSuccessMsg(t('salesInvoice.invoicePrintedSuccess') || 'Invoice printed successfully');
        }
      }
    } catch {
      setError(t('salesInvoice.failedToPrint') || 'Failed to print invoice');
    } finally {
      setPrintLoading(false);
    }
  };

  /**
   * Main Print Bill handler — routes to PDF viewer or POS printer
   * based on the company's DefaultPrintOutput setting.
   * BOTH: opens PDF tab first, then also sends to printer.
   */
  const handlePrint = async () => {
    if (!invoice) return;
    if (defaultPrintOutput === PrintOutputType.POS_PRINTER) {
      await sendToPosPrinter();
    } else if (defaultPrintOutput === PrintOutputType.BOTH) {
      openPdfTab(); // fire-and-forget (async, user-gesture already consumed)
      await sendToPosPrinter();
    } else {
      // PDF (default) — open in browser tab
      await openPdfTab();
    }
  };

  /** Always opens the PDF in a browser tab regardless of print mode setting. */
  const handleDownloadPdf = async () => {
    await openPdfTab();
  };

  /** Download a reprint with copy number watermark. */
  const handleReprint = async () => {
    if (!invoice) return;
    setReprintLoading(true);
    try {
      await salesInvoiceService.reprintInvoicePdf(invoice.id, invoice.invoiceNumber);
    } catch {
      setError(t('salesInvoice.failedToPrint'));
    } finally {
      setReprintLoading(false);
    }
  };

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  const fmt = useCurrencyFormat();

  const fmtDate = (d: string) => new Date(d).toLocaleDateString(langCode);

  const getStepClass = (index: number): string => {
    if (!invoice) return 'ims-step';
    const statusOrder: SalesInvoiceStatus[] = ['Draft', 'Issued', 'PartiallyPaid', 'Paid'];
    const currentIdx = statusOrder.indexOf(invoice.status as SalesInvoiceStatus);
    if (index < currentIdx)  return 'ims-step ims-step--done';
    if (index === currentIdx) return 'ims-step ims-step--active';
    return 'ims-step';
  };

  // ── Loading / error states ────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="si-detail-page">
          <div className="ims-loading-state">
            <div className="ims-spinner" />
            <p>{t('common.loading')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error && !invoice) {
    return (
      <Layout>
        <div className="si-detail-page" style={{ padding: 24 }}>
          <div className="ims-alert ims-alert-error">{error}</div>
          <button className="btn-cancel" style={{ marginTop: 16 }} onClick={() => navigate('/sales/sales-invoices')}>
            ← {t('salesInvoice.backToList')}
          </button>
        </div>
      </Layout>
    );
  }

  if (!invoice) {
    return (
      <Layout>
        <div className="si-detail-page" style={{ padding: 24 }}>
          <div className="ims-alert ims-alert-error">{t('salesInvoice.notFound')}</div>
          <button className="btn-cancel" style={{ marginTop: 16 }} onClick={() => navigate('/sales/sales-invoices')}>
            ← {t('salesInvoice.backToList')}
          </button>
        </div>
      </Layout>
    );
  }

  const canIssue       = invoice.status === 'Draft';
  const canEdit        = invoice.status === 'Draft';
  const canRecordPay   = invoice.status === 'Issued' || invoice.status === 'PartiallyPaid' || invoice.status === 'Overdue';
  const canCancel      = invoice.status !== 'Cancelled' && invoice.status !== 'Voided' && invoice.status !== 'Paid';
  // Unlike cancel, void covers Paid invoices — reversing a rung-up sale is the common
  // counter correction. The server still refuses if the trading day is closed.
  const canVoid        = invoice.status !== 'Voided' && invoice.status !== 'Cancelled'
                         && invoice.status !== 'Draft'
                         && hasPermission(Permissions.SalesInvoices.Void);

  return (
    <Layout>
      <div className="si-detail-page">

        {/* Voided invoices lead with why — otherwise the reason is buried in the audit log */}
        {invoice.status === 'Voided' && invoice.voidReason && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 12 }}>
            <Ban size={15} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>
              <strong>{t('salesInvoice.voidedBanner')}</strong> {invoice.voidReason}
              {invoice.voidedByName ? ` — ${invoice.voidedByName}` : ''}
              {invoice.voidedAt ? ` (${new Date(invoice.voidedAt).toLocaleString()})` : ''}
            </span>
          </div>
        )}

        {/* ── Hero ── */}
        <div className="ims-hero">
          <div className="ims-hero-nav">
            <button className="ims-hero-back" onClick={() => navigate('/sales/sales-invoices')}>
              ← {t('salesInvoice.backToList')}
            </button>
            {/* Installment plans only apply to credit sales — the plan settles the balance. */}
            {invoice.isCredit && (
              <button
                className="ims-hero-back"
                onClick={() => navigate(`/sales/sales-invoices/${invoice.id}/installment-plan`)}
              >
                <CalendarClock size={13} strokeWidth={2} /> {t('installment.title')}
              </button>
            )}
          </div>
          <div className="ims-hero-content">
            <div>
              <h1 className="ims-hero-number">{invoice.invoiceNumber}</h1>
              <p className="ims-hero-supplier">{invoice.customerName}</p>
              <div className="ims-hero-badges">
                <span className={STATUS_CHIP_CLASS[invoice.status] ?? 'si-status-chip si-status-chip--draft'}>
                  {t(`salesInvoice.status${invoice.status}`)}
                </span>
                {invoice.isCredit && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '3px 10px',
                    borderRadius: 999,
                    background: 'rgba(6,182,212,0.15)',
                    color: '#ecfeff',
                    fontSize: 12,
                    fontWeight: 600,
                    border: '1px solid rgba(6,182,212,0.4)',
                  }}>
                    <CreditCard size={12} strokeWidth={2.5} />
                    {t('salesInvoice.creditSale') || 'Credit Sale'}
                  </span>
                )}
              </div>
            </div>
            <div className="si-hero-right">
              <div className="si-hero-icon-circle">
                <CircleDollarSign size={30} strokeWidth={1.5} />
              </div>
              <div className="si-hero-dates">
                <div className="si-hero-date-item">
                  <Calendar size={12} className="si-hero-date-item-icon" />
                  <div>
                    <span className="si-hero-date-item-label">{t('salesInvoice.invoiceDate')}</span>
                    <span className="si-hero-date-item-value">{fmtDate(invoice.invoiceDate)}</span>
                  </div>
                </div>
                <div className="si-hero-date-item">
                  <Calendar size={12} className="si-hero-date-item-icon" />
                  <div>
                    <span className="si-hero-date-item-label">{t('salesInvoice.dueDate')}</span>
                    <span className="si-hero-date-item-value">{fmtDate(invoice.dueDate)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Workflow steps ── */}
        {(invoice.status === 'Draft' || invoice.status === 'Issued' || invoice.status === 'PartiallyPaid' || invoice.status === 'Paid') && (
          <div className="ims-steps">
            {STEPS.map((step, i) => (
              <React.Fragment key={step.label}>
                <div className={getStepClass(i)}>
                  <div className="ims-step-dot">{i + 1}</div>
                  {t(`salesInvoice.step${step.label.replace(/\s/g, '')}`)}
                </div>
                {i < STEPS.length - 1 && <span className="ims-step-arrow">›</span>}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* ── Inline alerts ── */}
        {(successMsg || error) && (
          <div style={{ padding: '0 24px' }}>
            {successMsg && (
              <div className="ims-alert ims-alert-success" style={{ marginBottom: 12 }}>
                <Check size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
                <span>{successMsg}</span>
                <button type="button" onClick={() => setSuccessMsg(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>✕</button>
              </div>
            )}
            {error && (
              <div className="ims-alert ims-alert-error" style={{ marginBottom: 12 }}>
                <AlertCircle size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
                <span>{error}</span>
                <button type="button" onClick={() => setError(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>✕</button>
              </div>
            )}
          </div>
        )}

        {/* ── Body ── */}
        <div className="ims-detail-body">

          {/* ── Main panel ── */}
          <div className="ims-detail-main">

            {/* Invoice info card */}
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">{t('salesInvoice.invoiceInfo')}</span>
              </div>
              <div className="ims-info-grid">
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('salesInvoice.customer')}</span>
                  <span className="ims-info-value">{invoice.customerName}</span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('salesInvoice.invoiceDate')}</span>
                  <span className="ims-info-value">{fmtDate(invoice.invoiceDate)}</span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('salesInvoice.dueDate')}</span>
                  <span className="ims-info-value">{fmtDate(invoice.dueDate)}</span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('salesInvoice.paymentStatus')}</span>
                  <span className="ims-info-value">{t(`salesInvoice.payment${invoice.paymentStatus}`)}</span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('salesInvoice.createdOn')}</span>
                  <span className="ims-info-value">{fmtDate(invoice.createdAt)}</span>
                </div>
                {invoice.referenceNumber && (
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('salesInvoice.referenceNumber')}</span>
                    <span className="ims-info-value">{invoice.referenceNumber}</span>
                  </div>
                )}
                {invoice.notes && (
                  <div className="ims-info-field" style={{ gridColumn: '1 / -1' }}>
                    <span className="ims-info-label">{t('salesInvoice.notes')}</span>
                    <span className="ims-info-value" style={{ whiteSpace: 'pre-wrap' }}>{invoice.notes}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Line items card */}
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">
                  {t('salesInvoice.lineItems')} ({invoice.lines.length})
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="ims-table">
                  <thead>
                    <tr>
                      <th className="ims-th">{t('salesInvoice.product')}</th>
                      <th className="ims-th ims-th--num">{t('salesInvoice.quantity')}</th>
                      <th className="ims-th ims-th--num">{t('salesInvoice.unitPrice')}</th>
                      <th className="ims-th ims-th--num">{t('salesInvoice.discount')}</th>
                      <th className="ims-th ims-th--num">{t('salesInvoice.taxRate')}</th>
                      <th className="ims-th ims-th--num">{t('salesInvoice.taxAmount')}</th>
                      <th className="ims-th ims-th--num">{t('salesInvoice.lineTotal')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.lines.map((line) => (
                      <tr key={line.id} className="ims-tr">
                        <td className="ims-td">
                          <div style={{ fontWeight: 600 }}>
                            {line.lineType === 'Service'
                              ? (line.description || line.productName || t('salesInvoice.serviceItem'))
                              : (line.productName || line.description)}
                          </div>
                          {line.lineType !== 'Service' && line.productSKU && (
                            <div style={{ fontSize: 11, color: 'var(--ims-text-muted)' }}>{line.productSKU}</div>
                          )}
                          {line.lineType === 'Service' && (
                            <div style={{ fontSize: 11, color: 'var(--ims-accent)', fontStyle: 'italic' }}>
                              {t('salesInvoice.serviceLabel')}
                            </div>
                          )}
                        </td>
                        <td className="ims-td ims-td--num" style={{ textAlign: 'right' }}>{line.quantity}</td>
                        <td className="ims-td ims-td--num" style={{ textAlign: 'right' }}>{fmt(line.unitPrice)}</td>
                        <td className="ims-td ims-td--num" style={{ textAlign: 'right' }}>
                          {line.discountAmount > 0 ? (
                            <span style={{ color: 'var(--ims-amber-text)' }}>{fmt(line.discountAmount)}</span>
                          ) : '—'}
                        </td>
                        <td className="ims-td ims-td--num" style={{ textAlign: 'right' }}>
                          {line.taxRate > 0 ? `${line.taxRate}%` : '—'}
                        </td>
                        <td className="ims-td ims-td--num" style={{ textAlign: 'right' }}>
                          {line.taxAmount > 0 ? fmt(line.taxAmount) : '—'}
                        </td>
                        <td className="ims-td ims-td--num" style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(line.lineTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="ims-tr">
                      <td colSpan={6} style={{ textAlign: 'right' }} className="si-tfoot-label ims-td ims-td--num">
                        {t('salesInvoice.subtotal')}
                      </td>
                      <td style={{ textAlign: 'right' }} className="si-tfoot-label ims-td ims-td--num">{fmt(invoice.subtotal)}</td>
                    </tr>
                    {invoice.discountAmount > 0 && (
                      <tr className="si-tfoot-row">
                        <td colSpan={6} style={{ textAlign: 'right' }} className="si-tfoot-label ims-td ims-td--num">
                          {t('salesInvoice.discount')}
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--ims-amber-text)', fontWeight: 600 }} className="ims-td ims-td--num">
                          -{fmt(invoice.discountAmount)}
                        </td>
                      </tr>
                    )}
                    {invoice.taxAmount > 0 && (
                      <tr className="si-tfoot-row">
                        <td colSpan={6} style={{ textAlign: 'right' }} className="si-tfoot-label ims-td ims-td--num">
                          {t('salesInvoice.tax')}
                        </td>
                        <td style={{ textAlign: 'right' }} className="si-tfoot-label ims-td ims-td--num">{fmt(invoice.taxAmount)}</td>
                      </tr>
                    )}
                    <tr className="si-tfoot-row">
                      <td colSpan={6} style={{ textAlign: 'right' }} className="si-tfoot-total ims-td ims-td--num">
                        {t('salesInvoice.totalAmount')}
                      </td>
                      <td style={{ textAlign: 'right' }} className="si-tfoot-total ims-td ims-td--num">{fmt(invoice.totalAmount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Payment history card */}
            {(payments.length > 0 || invoice.amountPaid > 0) && (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    {t('salesInvoice.paymentHistory')}
                    {payments.length > 0 && (
                      <span style={{ marginInlineStart: 8, fontSize: 12, color: 'var(--ims-text-muted)', fontWeight: 400 }}>
                        ({payments.length})
                      </span>
                    )}
                  </span>
                </div>
                {paymentsLoading ? (
                  <div className="ims-card-body" style={{ textAlign: 'center', color: 'var(--ims-text-muted)' }}>
                    {t('common.loading')}
                  </div>
                ) : payments.length === 0 ? (
                  <div className="ims-card-body" style={{ color: 'var(--ims-text-muted)', fontSize: 13 }}>
                    {t('salesInvoice.noPaymentsRecorded')}
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="ims-table">
                      <thead>
                        <tr>
                          <th>{t('salesInvoice.paymentDate')}</th>
                          <th>{t('salesInvoice.paymentMethod')}</th>
                          <th>{t('salesInvoice.referenceNumber')}</th>
                          <th style={{ textAlign: 'right' }}>{t('salesInvoice.paymentAmount')}</th>
                          <th style={{ textAlign: 'right' }}>{t('salesInvoice.amountTendered')}</th>
                          <th style={{ textAlign: 'right' }}>{t('salesInvoice.changeAmount')}</th>
                          <th>{t('salesInvoice.recordedBy')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((p) => (
                          <tr key={p.id}>
                            <td>{fmtDate(p.paymentDate)}</td>
                            <td>
                              <span className="si-payment-method-badge">
                                {paymentMethodName(p.paymentMethod)}
                              </span>
                            </td>
                            <td style={{ color: 'var(--ims-text-sub)', fontSize: 12 }}>
                              {p.referenceNumber || '—'}
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--ims-green-text)' }}>
                              {fmt(p.amountPaid)}
                            </td>
                            <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--ims-text-muted)' }}>
                              {p.tenderedAmount !== undefined && p.tenderedAmount !== null ? fmt(p.tenderedAmount) : '—'}
                            </td>
                            <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--ims-text-muted)' }}>
                              {p.changeGiven !== undefined && p.changeGiven !== null ? fmt(p.changeGiven) : '—'}
                            </td>
                            <td style={{ fontSize: 12, color: 'var(--ims-text-muted)' }}>
                              {p.recordedByUserName || `#${p.recordedByUserId}`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="si-tfoot-row">
                          <td colSpan={3} style={{ textAlign: 'right' }} className="si-tfoot-label">
                            {t('salesInvoice.totalPaid')}
                          </td>
                          <td style={{ textAlign: 'right' }} className="si-tfoot-total">
                            {fmt(payments.reduce((s, p) => s + p.amountPaid, 0))}
                          </td>
                          <td colSpan={3} />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}

          </div>

          {/* ── Sidebar ── */}
          <div className="ims-detail-sidebar">

            {/* Payment summary card */}
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">{t('salesInvoice.paymentSummary')}</span>
              </div>
              <div className="ims-card-body">
                <div className="si-payment-summary">
                  <div className="si-payment-row">
                    <span>{t('salesInvoice.totalAmount')}</span>
                    <strong>{fmt(invoice.totalAmount)}</strong>
                  </div>
                  <div className="si-payment-row si-payment-row--paid">
                    <span>{t('salesInvoice.amountPaid')}</span>
                    <strong>{fmt(invoice.amountPaid)}</strong>
                  </div>
                  <div className="si-balance-due-block">
                    <span className="si-balance-due-label">{t('salesInvoice.balanceDue')}</span>
                    <span className={`si-balance-due-amount${invoice.balanceDue <= 0 ? ' si-balance-due-amount--zero' : ''}`}>
                      {fmt(invoice.balanceDue)}
                    </span>
                  </div>
                </div>

                {/* Credit sale: settle via Outstanding Invoices shortcut */}
                {canRecordPay && invoice.isCredit && (
                  <div style={{ marginTop: 12 }}>
                    <button
                      className="ims-btn ims-btn-primary"
                      style={{ width: '100%', marginBottom: 8 }}
                      onClick={() => navigate('/sales/sales-invoices/outstanding')}
                    >
                      {t('salesInvoice.settleInOutstanding') || 'Settle in Outstanding Invoices'}
                    </button>
                  </div>
                )}

                {/* Record payment inline form */}
                {canRecordPay && (
                  <div style={{ marginTop: invoice.isCredit ? 0 : 16 }}>
                    {!showPaymentForm ? (
                      <button
                        className="ims-btn ims-btn-success"
                        style={{ width: '100%' }}
                        onClick={() => setShowPaymentForm(true)}
                        disabled={actionLoading}
                      >
                        <CircleDollarSign  size={18} strokeWidth={1.75} />
                        {invoice.isCredit
                          ? (t('salesInvoice.recordPartialPayment') || 'Record Partial Payment')
                          : t('salesInvoice.recordPayment')}
                      </button>
                    ) : (
                      <div className="si-payment-form">
                        <p className="si-payment-form-title">{t('salesInvoice.recordPayment')}</p>
                        <div className="ims-form-group">
                          <label className="ims-form-label">{t('salesInvoice.paymentMethod')}</label>
                          <select
                            className="ims-form-control"
                            value={paymentData.paymentMethod}
                            onChange={(e) => setPaymentData((p) => ({ ...p, paymentMethod: e.target.value }))}
                          >
                            <option value="">{t('salesInvoice.selectPaymentMethod')}</option>
                            {activePaymentTypes.map((pt) => (
                              <option key={pt.id} value={pt.code}>{pt.name}</option>
                            ))}
                          </select>
                        </div>
                        {isCashPaymentMethod ? (
                          <>
                            <div className="ims-form-group">
                              <label className="ims-form-label">
                                {t('salesInvoice.amountTendered')} <span className="required">*</span>
                              </label>
                              <input
                                type="number"
                                className="ims-form-control"
                                value={amountTendered || ''}
                                min={0.01}
                                step="0.01"
                                onChange={(e) => setAmountTendered(parseFloat(e.target.value) || 0)}
                              />
                              <div className="si-quick-amounts">
                                {[100, 500, 1000, 5000].map((amt) => (
                                  <button
                                    key={amt}
                                    type="button"
                                    className="si-quick-amt-btn"
                                    onClick={() => setAmountTendered(amt)}
                                  >
                                    {amt.toLocaleString()}
                                  </button>
                                ))}
                              </div>
                              {paymentError && <span style={{ color: 'var(--color-danger)', fontSize: 12 }}>{paymentError}</span>}
                            </div>
                            <div className="si-payment-row" style={{
                              background: 'var(--ims-green-50, #f0fdf4)',
                              borderRadius: 6,
                              padding: '8px 10px',
                              marginTop: 4,
                              marginBottom: 12,
                            }}>
                              <span style={{ fontWeight: 600 }}>{t('salesInvoice.changeAmount')}</span>
                              <strong style={{ color: 'var(--ims-green-700, #15803d)', fontSize: 16 }}>
                                {fmt(Math.max(0, amountTendered - invoice.balanceDue))}
                              </strong>
                            </div>
                          </>
                        ) : (
                          <div className="ims-form-group">
                            <label className="ims-form-label">
                              {t('salesInvoice.paymentAmount')} <span className="required">*</span>
                            </label>
                            <input
                              type="number"
                              className="ims-form-control"
                              value={paymentData.amountPaid || ''}
                              min={0.01}
                              max={invoice.balanceDue}
                              step="0.01"
                              onChange={(e) => setPaymentData((p) => ({ ...p, amountPaid: parseFloat(e.target.value) || 0 }))}
                            />
                            {paymentError && <span style={{ color: 'var(--color-danger)', fontSize: 12 }}>{paymentError}</span>}
                          </div>
                        )}
                        <div className="ims-form-group">
                          <label className="ims-form-label">{t('salesInvoice.paymentDate')}</label>
                          <input
                            type="date"
                            className="ims-form-control"
                            value={paymentData.paymentDate}
                            onChange={(e) => setPaymentData((p) => ({ ...p, paymentDate: e.target.value }))}
                          />
                        </div>
                        <div className="ims-form-group">
                          <label className="ims-form-label">{t('salesInvoice.referenceNumber')}</label>
                          <input
                            type="text"
                            className="ims-form-control"
                            placeholder={t('salesInvoice.referencePlaceholder')}
                            value={paymentData.referenceNumber}
                            onChange={(e) => setPaymentData((p) => ({ ...p, referenceNumber: e.target.value }))}
                          />
                        </div>
                        <div className="si-payment-form-footer">
                          <button
                            className="btn-cancel"
                            onClick={() => { setShowPaymentForm(false); setPaymentError(null); setAmountTendered(0); }}
                            disabled={actionLoading}
                          >
                            {t('common.cancel')}
                          </button>
                          <button
                            className="ims-btn ims-btn-success"
                            onClick={handleRecordPayment}
                            disabled={actionLoading}
                          >
                            {actionLoading ? <span className="ims-btn-spinner" /> : null}
                            {t('salesInvoice.savePayment')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Actions card */}
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">{t('common.actions')}</span>
              </div>
              <div className="ims-card-body">
                <div className="ims-actions-list">

                  {canIssue && (
                    <button
                      className="ims-action-btn ims-action-btn--approve"
                      onClick={handleIssue}
                      disabled={actionLoading}
                    >
                      {actionLoading ? <span className="ims-btn-spinner" /> : (
                        <Send size={15} strokeWidth={2} />
                      )}
                      {t('salesInvoice.issueInvoice')}
                    </button>
                  )}

                  {canEdit && (
                    <button
                      className="ims-action-btn ims-action-btn--edit"
                      onClick={() => navigate(`/sales/sales-invoices/${invoice.id}/edit`)}
                      disabled={actionLoading}
                    >
                      <PenLine size={15} strokeWidth={2} />
                      {t('common.edit')}
                    </button>
                  )}

                  {canCancel && (
                    <button
                      className="ims-action-btn ims-action-btn--reject"
                      onClick={handleCancel}
                      disabled={actionLoading}
                    >
                      <XCircle size={15} strokeWidth={2} />
                      {t('salesInvoice.cancelInvoice')}
                    </button>
                  )}

                  {canVoid && (
                    <button
                      className="ims-action-btn ims-action-btn--reject"
                      onClick={() => { setVoidReason(''); setVoidError(null); setShowVoidModal(true); }}
                      disabled={actionLoading}
                    >
                      <Ban size={15} strokeWidth={2} />
                      {t('salesInvoice.voidInvoice')}
                    </button>
                  )}

                  <button
                    className="ims-action-btn ims-action-btn--download"
                    onClick={handleDownloadPdf}
                    disabled={printLoading}
                    title="Download invoice as PDF"
                  >
                    <Download size={15} strokeWidth={2} />
                    {t('salesInvoice.downloadPdf') || 'Download PDF'}
                  </button>

                  <button
                    className="ims-action-btn ims-action-btn--download"
                    onClick={handleReprint}
                    disabled={reprintLoading}
                    title={t('salesInvoice.reprintTitle') || 'Download a numbered copy'}
                  >
                    {reprintLoading ? <span className="ims-btn-spinner" /> : <Download size={15} strokeWidth={2} />}
                    {t('salesInvoice.reprint') || 'Reprint (Copy)'}
                  </button>

                  {usesPosPrinter && printerProfiles.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label
                        htmlFor="si-printer-select"
                        style={{ fontSize: '0.72rem', fontWeight: 500, color: 'var(--ims-text-sub)' }}
                      >
                        {t('salesInvoice.printer') || 'Printer'}
                      </label>
                      <select
                        id="si-printer-select"
                        className="ims-form-control"
                        value={selectedPrinterId ?? ''}
                        disabled={printLoading}
                        onChange={(e) => setSelectedPrinterId(Number(e.target.value))}
                      >
                        {printerProfiles.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                            {p.printerType === PrinterType.Thermal
                              ? ` (${p.paperWidthMm}mm)`
                              : ' (A4)'}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <button
                    className="ims-action-btn ims-action-btn--print"
                    onClick={handlePrint}
                    disabled={printLoading}
                    title={
                      defaultPrintOutput === PrintOutputType.POS_PRINTER
                        ? 'Send to POS printer'
                        : defaultPrintOutput === PrintOutputType.BOTH
                        ? 'Open PDF + send to POS printer'
                        : 'View bill as PDF'
                    }
                  >
                    {printLoading ? (
                      <span className="ims-btn-spinner" />
                    ) : defaultPrintOutput === PrintOutputType.POS_PRINTER || defaultPrintOutput === PrintOutputType.BOTH ? (
                      <Printer size={15} strokeWidth={2} />
                    ) : (
                      <FileText size={15} strokeWidth={2} />
                    )}
                    {defaultPrintOutput === PrintOutputType.POS_PRINTER
                      ? (t('salesInvoice.printBillPos') || 'Print Bill')
                      : defaultPrintOutput === PrintOutputType.BOTH
                      ? (t('salesInvoice.printBillBoth') || 'Print Bill')
                      : (t('salesInvoice.printBill') || 'Print Bill')}
                  </button>

                </div>
              </div>
            </div>

            {/* Quick link back to list */}
            <div className="ims-card">
              <div className="ims-card-body">
                <button
                  className="detail-quick-link"
                  onClick={() => navigate('/sales/sales-invoices')}
                >
                  <List size={16} strokeWidth={2} />
                  {t('salesInvoice.viewAllInvoices')}
                </button>
              </div>
            </div>

          </div>
        </div>

      </div>

      <PrintWarningModal
        isOpen={showPrintWarning}
        warnings={printWarnings}
        onRetry={handlePrint}
        onContinue={() => {
          setShowPrintWarning(false);
          setSuccessMsg(t('salesInvoice.invoicePrintedSuccess') || 'Invoice printed successfully');
        }}
        onDownloadPdf={() => {
          setShowPrintWarning(false);
          handleDownloadPdf();
        }}
        onClose={() => {
          setShowPrintWarning(false);
          setPrintWarnings([]);
        }}
        isLoading={printLoading}
      />

      {/* ── Void confirmation ── */}
      {showVoidModal && (
        <div
          className="ims-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="void-modal-title"
          onClick={() => !actionLoading && setShowVoidModal(false)}
        >
          <div className="ims-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="ims-card-header">
              <h3 className="ims-card-title" id="void-modal-title">
                {t('salesInvoice.voidInvoice')} — {invoice.invoiceNumber}
              </h3>
            </div>
            <div className="ims-card-body">
              <p style={{ marginTop: 0 }}>{t('salesInvoice.voidWarning')}</p>

              <div className="ims-form-group">
                <label className="ims-form-label" htmlFor="void-reason">
                  {t('salesInvoice.voidReason')} <span className="required">*</span>
                </label>
                <textarea
                  id="void-reason"
                  className="ims-form-control ims-textarea"
                  rows={3}
                  value={voidReason}
                  autoFocus
                  onChange={(e) => { setVoidReason(e.target.value); setVoidError(null); }}
                  placeholder={t('salesInvoice.voidReasonPlaceholder')}
                />
              </div>

              {voidError && (
                <div className="ims-alert ims-alert-error" style={{ marginBottom: 12 }}>
                  <AlertCircle size={15} strokeWidth={2} style={{ flexShrink: 0 }} />
                  <span>{voidError}</span>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="ims-btn ims-btn-secondary"
                  onClick={() => setShowVoidModal(false)}
                  disabled={actionLoading}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="ims-btn ims-btn-danger"
                  onClick={handleVoid}
                  disabled={actionLoading || !voidReason.trim()}
                >
                  {actionLoading ? <span className="ims-btn-spinner" /> : <Ban size={14} strokeWidth={2} />}
                  {t('salesInvoice.confirmVoid')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default SalesInvoiceDetailPage;
